import { Avatar, Box } from '@cambridgeassessment/cambridge-ui';
import { Typography } from '@material-ui/core';
import React, { memo, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useOrgAdmins from 'src/hooks/useOrgAdmins';
import { OrgUser } from 'src/types';
import { filterOrgUsers } from 'src/utils/filterOrgUsers';
import { getUserName } from 'src/utils/transformers';
import OrgUserStatus from '../OrgUserStatus/OrgUserStatus';
import Table from '../Table/Table';
import UserStatusDescriptionCard from '../TooltipDescriptionCard/UserStatusDescriptionCard';
import { headCells } from './constants';
import { useStylesTable } from './styles';
import { Columns, Row } from './types';
import { sortColumns } from './utils';

const tooltips = {
  status: <UserStatusDescriptionCard />,
};

export const createRows = (
  admins: OrgUser[] = [],
  associateId: null | undefined | string
): Row[] =>
  admins.map((admin) => ({
    cells: {
      [Columns.FullName]: (
        <Box
          alignItems="center"
          display="flex"
          data-sortvalue={getUserName(admin)}
        >
          <Box mr={2}>
            <Avatar
              src={admin.profilePicture}
              data-testid="avatar-picture"
              size="xs"
            />
          </Box>
          <Typography variant="body1" data-testid="userName-typography">
            {getUserName(admin)}
          </Typography>
        </Box>
      ),
      [Columns.Status]: (
        <OrgUserStatus status={admin.status} data-sortvalue={admin.status} />
      ),
    },
    link: associateId
      ? `/organisation/admins/associate/child/${associateId}/admin/${admin.UID}`
      : `/organisation/admins/${admin.UID}`,
  }));
interface OrgAdminTableProps {
  orgId: string;
}
const OrgAdminTable: React.FC<OrgAdminTableProps> = ({ orgId }) => {
  const {
    admins,
    isLoading,
    error,
    tableSettings,
    searchQuery,
    updateTableSettings,
  } = useOrgAdmins();
  const { associateId } = useParams();

  const orgAdmins = filterOrgUsers(searchQuery, admins);

  const rows = useMemo(
    () => createRows(orgAdmins, associateId),
    [orgAdmins, associateId]
  );

  const classes = useStylesTable();
  const noResultsMessage = useMemo(
    () => (
      <Box>
        <Typography variant="body1">
          <Box display="flex" justifyContent="center">
            No results were found for&nbsp;
            <Typography variant="subtitle1">{searchQuery}</Typography>.
          </Box>
        </Typography>
        <Typography variant="body1">
          Check your spelling or try searching with another keyword.
        </Typography>
      </Box>
    ),
    [searchQuery]
  );

  return (
    <Table
      loading={isLoading}
      error={!!error}
      noResultsMessage={noResultsMessage}
      headCells={headCells}
      rows={rows}
      settings={tableSettings}
      sortColumns={sortColumns}
      updateSettings={updateTableSettings}
      data-testid="orgAdmin-table"
      tooltips={tooltips}
      className={classes.table}
    />
  );
};

export default memo(OrgAdminTable);
